import { createContext, useContext, useMemo } from 'react';

import {
  TConversionStatusBatchContext,
  TConversionStatusBatchProviderProps,
} from '@/console/features/projects/components/conversion-status-batch/conversion-status-batch-context.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadJobsByProjectIdsQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  FeatureEnum,
  JobFilterInput,
  JobFragment,
  ReadJobsByProjectIdsQuery,
} from '@/shared/types/types.generated';

const JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS = 30_000;

const ConversionStatusBatchContext =
  createContext<TConversionStatusBatchContext | null>(null);

export const ConversionStatusBatchProvider = ({
  projectIds,
  versionIds,
  children,
}: TConversionStatusBatchProviderProps) => {
  const { isJobRunning } = useJobStatus();

  const filter: JobFilterInput | undefined = useMemo(() => {
    if (versionIds.length === 0) return undefined;
    return {
      projectVersionIds: { containsAny: versionIds },
    };
  }, [versionIds]);

  const { data, isLoading } = usePollingQuery({
    useQueryHook: useReadJobsByProjectIdsQuery,
    queryArg: {
      projectIds,
      jobTypes: [FeatureEnum.ProjectConvert],
      filter,
      pagination: { page: 1, pageSize: versionIds.length * 2 },
    },
    shouldStopPolling: (data: ReadJobsByProjectIdsQuery) =>
      data.readJobsByProjectIds.items.every(
        (item) => !isJobRunning(item.status),
      ),
    pollingInterval: JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS,
    queryOptions: {
      skip: projectIds.length === 0 || versionIds.length === 0,
    },
  });

  const jobsByVersion = useMemo(() => {
    const map = new Map<string, JobFragment[]>();
    for (const item of data?.readJobsByProjectIds?.items ?? []) {
      for (const vid of item.projectVersionIds ?? []) {
        const existing = map.get(vid);
        if (existing) {
          existing.push(item);
        } else {
          map.set(vid, [item]);
        }
      }
    }
    return map;
  }, [data]);

  const value: TConversionStatusBatchContext = useMemo(
    () => ({
      getJobsForVersion: (versionId: string) =>
        jobsByVersion.get(versionId) ?? [],
      isLoading,
    }),
    [jobsByVersion, isLoading],
  );

  return (
    <ConversionStatusBatchContext.Provider value={value}>
      {children}
    </ConversionStatusBatchContext.Provider>
  );
};

export const useConversionStatusBatch = () =>
  useContext(ConversionStatusBatchContext);
